<script setup lang="ts">
import { max, scaleBand, scaleLinear, type ScaleLinear } from 'd3';
import type { TooltipData } from '../tooltip/index.vue';
import yAxisModule from './modules/yAxis.vue';
import xAxisModule from './modules/xAxis.vue';
import gridModule from './modules/grid.vue';
import lineModule from './modules/line.vue';
import tooltipModule from './modules/tooltip.vue';
import areaModule from './modules/area.vue';
import { useResizeObserver } from '@vueuse/core';

export interface LineChartOptions {
  height?: string;
  grace?: number;
  unit?: string;
  tooltip?: (dataIndex: number) => TooltipData | undefined;
}

export interface LineChartData {
  keys: string[];
  areas?: { label?: string; color?: string; borderColor?: string; dashed?: boolean; startDate: string; endDate: string }[];
  lines: { color?: string; dashed?: boolean; values: number[] }[];
}

// ================================================ Props
const props = defineProps<{
  data: LineChartData;
  loading: boolean;
  options: LineChartOptions;
}>();

// ================================================ Data
const chartContainer = useTemplateRef<HTMLElement>('chartContainer');
const containerWidth = ref(0);
const rightMargin = ref(25);
const bottomMargin = ref(0);
const leftMargin = ref(25);
const activePointIndex = ref<number>(-1);

// ================================================ Computed
const chartStyle = computed(() => ({
  '--marginTop': '16px',
  '--marginRight': `${rightMargin.value}px`,
  '--marginBottom': `${bottomMargin.value}px`,
  '--marginLeft': `${leftMargin.value}px`,
}));

// ================================================ Scales
const xScale = computed(() => scaleBand().domain(props.data.keys).range([0, 100]).padding(0.4));
const yScale = computed(() => {
  const grace = props.options?.grace ?? 0.2;
  return calculateScale(
    props.data.lines.flatMap((d) => d.values),
    grace,
  );
});

// Generate ticks pour respecter la grâce calculée
const generateTicks = (scale: ScaleLinear<number, number>, numTicks = 8) => {
  const domain = scale.domain();
  const min = domain[0] || 0;
  const max = domain[1] || 0;
  if (min === max) return [0];

  const range = max - min;
  const step = range / (numTicks - 1);

  return Array.from({ length: numTicks }, (_, i) => min + i * step);
};

const yTicks = computed(() => generateTicks(yScale.value, 8));

// ================================================ Methods
const handleHoverPoint = (index: number) => {
  activePointIndex.value = index;
};

const handleUnhoverPoint = () => {
  activePointIndex.value = -1;
};
const calculateScale = (values: number[], graceFactor = 0.1) => {
  const maxValue = max(values) ?? 0;
  const minValue = Math.min(0, ...values);

  if (maxValue <= 0 && minValue >= 0) return scaleLinear().domain([0, 10]).range([100, 0]);

  // Calcul avec le grace
  const targetMax = maxValue * (1 + graceFactor);
  const targetMin = minValue < 0 ? minValue * (1 + graceFactor) : minValue;

  // Trouver l'ordre de grandeur et un diviseur approprié
  const maxMagnitude = Math.floor(Math.log10(Math.abs(targetMax || 1)));
  const minMagnitude = minValue < 0 ? Math.floor(Math.log10(Math.abs(targetMin))) : 0;
  const magnitude = Math.max(maxMagnitude, minMagnitude);
  const divisor = Math.pow(10, magnitude > 0 ? magnitude - 1 : magnitude);

  // Arrondir à un nombre "propre" juste supérieur à targetMax et inférieur à targetMin
  const roundedMax = Math.ceil(targetMax / divisor) * divisor;
  const roundedMin = minValue < 0 ? Math.floor(targetMin / divisor) * divisor : 0;

  return scaleLinear().domain([roundedMin, roundedMax]).range([100, 0]);
};

// ================================================ Lifecycle
useResizeObserver(chartContainer, (entries) => {
  const entry = entries[0];
  if (!entry) return;
  const { width } = entry.contentRect;
  containerWidth.value = width;
});
</script>
<template>
  <div ref="chartContainer">
    <template v-if="loading">
      <div class="flex items-center flex-col gap-4 justify-center w-full" :class="props.options.height">
        <ui-loader class="h-10 w-10" />
        <p class="text-gray-400 text-sm">{{ $t('charts.loading_data') }}</p>
      </div>
    </template>
    <div v-else class="relative w-full grid mt-0 mr-[25px]" :class="props.options.height" :style="chartStyle">
      <y-axis-module v-model="leftMargin" :loading="props.loading" :y-scale="yScale" :options="props.options" :custom-ticks="yTicks" />
      <div
        class="absolute inset-0 h-[calc(100%-var(--marginTop)-var(--marginBottom))] w-[calc(100%-var(--marginLeft)-var(--marginRight))] translate-x-[var(--marginLeft)] translate-y-[var(--marginTop)] overflow-visible"
      >
        <div class="relative w-full h-full">
          <x-axis-module
            v-model="bottomMargin"
            class="z-[0]"
            :container-width="containerWidth"
            :keys="props.data.keys"
            :loading="props.loading"
            :options="props.options"
          />
          <grid-module class="z-10" :loading="props.loading" :y-scale="yScale" :custom-ticks="yTicks" />
          <line-module
            class="z-20"
            :lines="props.data.lines"
            :keys="props.data.keys"
            :loading="props.loading"
            :x-scale="xScale"
            :y-scale="yScale"
          />
          <area-module class="-z-10" :areas="props.data.areas" :x-scale="xScale" />
          <tooltip-module
            v-if="props.options.tooltip"
            class="z-[3]"
            :keys="props.data.keys"
            :loading="props.loading"
            :x-scale="xScale"
            :y-scale="yScale"
            :tooltip="props.options.tooltip"
            @hover-point="handleHoverPoint"
            @unhover-point="handleUnhoverPoint"
          />
        </div>
      </div>
    </div>
  </div>
</template>
